<template>
  <div v-if="available" class="global-assistant" :style="fabStyle">
    <button v-if="!opened" ref="fab" class="assistant-fab" type="button" aria-label="AI 助手"
            @pointerdown="startDrag('fab', $event)" @click="onFabClick">
      <i class="el-icon-chat-dot-round" aria-hidden="true"/><span>AI 助手</span>
    </button>
    <transition name="assistant-drawer">
      <section v-show="opened" ref="panel" class="assistant-panel" :style="panelStyle" aria-label="全局 AI 助手">
        <header class="assistant-header" @pointerdown="startDrag('panel', $event)">
          <div><strong>AI 助手</strong><small>{{ currentSession ? contextLabel(currentSession) : '系统问答' }}</small></div>
          <button type="button" aria-label="关闭助手" @pointerdown.stop @click="close"><i class="el-icon-close"/></button>
        </header>

        <div class="assistant-toolbar">
          <el-select v-model="selectedSessionId" size="small" placeholder="选择会话" @change="selectSession">
            <el-option v-for="session in sessions" :key="session.id" :label="session.title || contextLabel(session)" :value="String(session.id)"/>
          </el-select>
          <el-button size="mini" @click="newGeneralSession">新建系统问答</el-button>
        </div>

        <div v-if="pageContext && currentSession && !sameContext(currentSession, pageContext)" class="assistant-switch">
          当前页面：{{ pageContext.machineName || `设备#${pageContext.machineId}` }}。
          <el-button type="text" size="mini" @click="newContextSession(pageContext)">切换到当前页面</el-button>
        </div>
        <div v-if="currentSession" class="assistant-context">
          {{ currentSession.machineId ? '默认设备上下文：' : '会话类型：' }}{{ contextLabel(currentSession) }}
          <span v-if="currentSession.diagnosisId"> · 诊断 {{ currentSession.diagnosisId }}</span>
          <span v-if="currentSession.alarmId"> · 报警 {{ currentSession.alarmId }}</span>
        </div>
        <div class="assistant-machine">
          <span>设备上下文（可选）：</span>
          <el-select v-model="chosenMachineId" size="small" filterable remote clearable placeholder="搜索授权设备"
                     :remote-method="searchMachines" :loading="machineLoading" @visible-change="machineVisible">
            <el-option v-for="machine in machines" :key="machine.machineId" :value="machine.machineId" :label="machine.machineName || `设备#${machine.machineId}`"/>
          </el-select>
          <el-button type="primary" size="mini" :disabled="!chosenMachineId || creating" @click="newMachineSession">开始</el-button>
        </div>
        <div v-if="machineError" class="assistant-error-line" @click="searchMachines(machineKeyword)">设备加载失败，点击重试</div>

        <div ref="messages" class="assistant-messages">
          <div v-if="!currentSession && !loading" class="assistant-empty">新建系统问答后，可直接按设备名询问监测数据、报警、点检、维修、备件或系统功能。</div>
          <div v-if="loading" class="assistant-empty">正在加载会话…</div>
          <div v-for="message in messages" :key="message.id || message.requestId || message.taskId"
               class="assistant-message" :class="message.role === 'user' ? 'is-user' : 'is-assistant'">
            <div class="assistant-bubble">
              <div class="assistant-content">{{ message.content }}</div>
              <div v-if="message.status === 'PENDING' || message.status === 'RUNNING'" class="assistant-state">正在处理问题…</div>
              <div v-if="message.status === 'FAILED'" class="assistant-state is-error">
                {{ message.error || '回复失败' }}
                <el-button v-if="message.taskId && message.pollError" type="text" size="mini" @click="pollTask(message.taskId, sessionEpoch)">继续查询</el-button>
                <el-button v-else-if="message.requestId" type="text" size="mini" @click="retryMessage(message)">重试</el-button>
              </div>
              <div v-if="message.sources && message.sources.length" class="assistant-sources">
                相关页面：<router-link v-for="(source, index) in safeSources(message.sources)" :key="index" :to="source.url">{{ source.label }}</router-link>
              </div>
              <time>{{ formatTime(message.createdAt) }}</time>
            </div>
          </div>
          <div v-if="panelError" class="assistant-error-line">{{ panelError }} <el-button type="text" size="mini" @click="refresh">重试加载</el-button></div>
        </div>

        <div class="assistant-quick">
          <button v-for="question in quickQuestions" :key="question" type="button" @click="useQuestion(question)">{{ question }}</button>
        </div>
        <footer class="assistant-compose">
          <el-input v-model="question" type="textarea" :rows="2" resize="none" :maxlength="1000" show-word-limit
                    placeholder="直接询问设备名、监测数据、报警、点检、维修、备件或系统使用"
                    @keydown.native="onInputKeydown"/>
          <div><small>Enter 发送 · Shift+Enter 换行</small><el-button type="primary" size="mini" :disabled="!currentSession || busy || !question.trim()" @click="send">发送</el-button></div>
        </footer>
      </section>
    </transition>
  </div>
</template>

<script>
import storage from '@/fetch/storage'
import {assistantBridge} from './bridge'
import {
  assistantData, createAssistantSession, listAssistantSessions,
  getAssistantHistory, sendAssistantMessage, getAssistantReply
} from '@/fetch/dtApi/assistant'
import {fetchGet} from '@/fetch/api'

const terminal = ['SUCCESS', 'FAILED']
const pageMachinePaths = ['/cockpit/deviceAgent/workbench', '/bigscreen/device', '/deviceTrend', '/sets/deviceModel', '/devicesNewDetails', '/devicesDetails', '/devicesNewEditDetails']
const allowedSourcePaths = ['/bigscreen/factory', '/bigscreen/line', '/bigscreen/device', '/cockpit/deviceAgent', '/monitor/dHistoryData', '/deviceTrend', '/phmalarm/dtDeviceAlarm', '/riifix/inspection/', '/riifix/repair/', '/riifix/spare-parts/', '/knowledgeBase/', '/sets/deviceModel', '/sets/dept', '/sets/post']
const delay = 2500

export default {
  name: 'GlobalAssistant',
  data() {
    return {
      opened: false, identityKey: '', identityEpoch: 0, sessionEpoch: 0,
      sessions: [], selectedSessionId: '', messages: [], question: '', panelError: '',
      loading: false, creating: false, sending: false, pollTimer: null,
      pageContext: null, machines: [], chosenMachineId: null, machineKeyword: '', machineLoading: false,
      machineError: false, machineSeq: 0,
      fabPosition: null, panelPosition: null, dragState: null, suppressFabClick: false, suppressClickTimer: null
    }
  },
  computed: {
    available() {
      const path = this.$route.path || ''
      return !!storage.get('token') && !path.includes('/login') && !path.startsWith('/bigscreen/') && !path.includes('/downloadPdf')
    },
    scopeVersion() { return this.$store.state.machineGroupScopeVersion },
    tenantId() { return this.$store.state.tenantId },
    currentSession() { return this.sessions.find(item => String(item.id) === this.selectedSessionId) },
    busy() { return this.sending || this.messages.some(item => item.role === 'assistant' &&
      (['PENDING', 'RUNNING'].includes(item.status) || item.pollError)) },
    quickQuestions() {
      if (this.currentSession && this.currentSession.machineId) {
        return ['这台设备最新的监测数据是什么？', '这台设备有哪些测点？', '这台设备最近有哪些报警和维修记录？']
      }
      return ['我能查看哪些设备？', '我有哪些待办点检任务？', '哪些备件库存不足？', '系统有哪些功能？']
    },
    fabStyle() {
      return this.fabPosition ? {left: `${this.fabPosition.left}px`, top: `${this.fabPosition.top}px`, right: 'auto', bottom: 'auto'} : {}
    },
    panelStyle() {
      return this.panelPosition ? {left: `${this.panelPosition.left}px`, top: `${this.panelPosition.top}px`, right: 'auto', bottom: 'auto'} : {}
    }
  },
  watch: {
    $route() {
      this.syncIdentity()
      this.pageContext = this.contextFromRoute()
      if (!this.available) { this.cancelDrag(); this.opened = false; this.stopPoll() }
    },
    scopeVersion() { this.syncIdentity() },
    tenantId() { this.syncIdentity() }
  },
  created() {
    this.syncIdentity()
    this.pageContext = this.contextFromRoute()
    assistantBridge.$on('open', this.open)
    window.addEventListener('storage', this.onStorage)
    window.addEventListener('setItem', this.onStorage)
    window.addEventListener('resize', this.onResize)
    window.addEventListener('blur', this.cancelDrag)
  },
  beforeDestroy() {
    assistantBridge.$off('open', this.open)
    window.removeEventListener('storage', this.onStorage)
    window.removeEventListener('setItem', this.onStorage)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('blur', this.cancelDrag)
    this.cancelDrag()
    clearTimeout(this.suppressClickTimer)
    this.stopPoll()
  },
  methods: {
    onFabClick(event) {
      if (this.suppressFabClick && event.detail !== 0) {
        this.suppressFabClick = false
        return
      }
      this.suppressFabClick = false
      this.open()
    },
    close() {
      this.cancelDrag()
      this.opened = false
      this.$nextTick(() => this.clampPosition('fab'))
    },
    startDrag(kind, event) {
      if (this.dragState || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return
      if (kind === 'panel' && event.target.closest && event.target.closest('button')) return
      if (kind === 'fab') {
        clearTimeout(this.suppressClickTimer)
        this.suppressClickTimer = null
        this.suppressFabClick = false
      }
      const element = this.$refs[kind]
      if (!element) return
      const rect = element.getBoundingClientRect()
      this.dragState = {
        kind, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
        left: rect.left, top: rect.top, width: rect.width, height: rect.height, moved: false
      }
      window.addEventListener('pointermove', this.onDragMove)
      window.addEventListener('pointerup', this.finishDrag)
      window.addEventListener('pointercancel', this.finishDrag)
    },
    onDragMove(event) {
      const drag = this.dragState
      if (!drag || event.pointerId !== drag.pointerId) return
      const dx = event.clientX - drag.startX
      const dy = event.clientY - drag.startY
      if (!drag.moved && Math.hypot(dx, dy) < 4) return
      drag.moved = true
      this[drag.kind === 'fab' ? 'fabPosition' : 'panelPosition'] = {
        left: Math.max(0, Math.min(drag.left + dx, window.innerWidth - drag.width)),
        top: Math.max(0, Math.min(drag.top + dy, window.innerHeight - drag.height))
      }
      if (event.cancelable) event.preventDefault()
    },
    finishDrag(event) {
      const drag = this.dragState
      if (!drag || event.pointerId !== drag.pointerId) return
      if (drag.kind === 'fab' && drag.moved) {
        this.suppressFabClick = true
        clearTimeout(this.suppressClickTimer)
        this.suppressClickTimer = setTimeout(() => { this.suppressFabClick = false; this.suppressClickTimer = null }, 500)
      }
      this.cancelDrag()
    },
    cancelDrag() {
      this.dragState = null
      window.removeEventListener('pointermove', this.onDragMove)
      window.removeEventListener('pointerup', this.finishDrag)
      window.removeEventListener('pointercancel', this.finishDrag)
    },
    clampPosition(kind) {
      const position = this[kind === 'fab' ? 'fabPosition' : 'panelPosition']
      const element = this.$refs[kind]
      if (!position || !element) return
      const rect = element.getBoundingClientRect()
      if (!rect.width || !rect.height) return
      this[kind === 'fab' ? 'fabPosition' : 'panelPosition'] = {
        left: Math.max(0, Math.min(position.left, window.innerWidth - rect.width)),
        top: Math.max(0, Math.min(position.top, window.innerHeight - rect.height))
      }
    },
    onResize() {
      this.cancelDrag()
      this.clampPosition('fab')
      this.clampPosition('panel')
    },
    identity() {
      const info = storage.get('infoMsg') || {}
      return [storage.get('token'), storage.get('accountId'), info.tenantId, info.userId,
        this.$store.state.tenantId, this.scopeVersion].join('|')
    },
    syncIdentity() {
      const next = this.identity()
      if (next === this.identityKey) return
      this.identityKey = next
      this.identityEpoch++
      this.sessionEpoch++
      this.stopPoll()
      this.cancelDrag()
      this.opened = false
      this.sessions = []
      this.selectedSessionId = ''
      this.messages = []
      this.question = ''
      this.panelError = ''
      this.sending = false
      this.loading = false
      this.creating = false
      this.machines = []
      this.machineLoading = false
      this.machineError = false
      this.chosenMachineId = null
      this.machineSeq++
      this.pageContext = this.contextFromRoute()
    },
    onStorage(event) {
      if (!event || ['token', 'infoMsg', 'accountId'].includes(event.key)) this.syncIdentity()
    },
    contextFromRoute() {
      const route = this.$route
      if (!pageMachinePaths.includes(route.path)) return null
      const id = route.query && route.query.machineId
      if (!/^[1-9]\d*$/.test(String(id || ''))) return null
      return {machineId: Number(id)}
    },
    sameContext(session, context) {
      return String(session.machineId || '') === String(context.machineId || '') &&
        String(session.diagnosisId || '') === String(context.diagnosisId || '') &&
        String(session.alarmId || '') === String(context.alarmId || '')
    },
    contextLabel(session) { return session.machineId ? (session.machineName || `设备#${session.machineId}`) : '系统问答' },
    async open(context) {
      this.syncIdentity()
      if (!this.available) return
      this.opened = true
      this.$nextTick(() => this.clampPosition('panel'))
      const target = context && context.machineId ? context : this.contextFromRoute()
      this.pageContext = target || null
      const epoch = this.identityEpoch
      await this.loadSessions(epoch)
      if (epoch !== this.identityEpoch) return
      if (this.selectedSessionId && (!context || !context.machineId || this.sameContext(this.currentSession, target))) {
        await this.selectSession(this.selectedSessionId)
      } else if (target) {
        const existing = this.sessions.find(item => this.sameContext(item, target))
        if (existing) await this.selectSession(String(existing.id))
        else await this.newContextSession(target)
      } else if (!this.selectedSessionId && this.sessions.length) {
        await this.selectSession(String(this.sessions[0].id))
      } else if (!this.selectedSessionId && !this.sessions.length && !this.panelError) {
        await this.newGeneralSession()
      }
    },
    async loadSessions(epoch = this.identityEpoch) {
      this.panelError = ''
      try {
        const list = assistantData(await listAssistantSessions())
        if (epoch !== this.identityEpoch) return
        this.sessions = Array.isArray(list) ? list : []
        if (this.selectedSessionId && !this.sessions.some(item => String(item.id) === this.selectedSessionId)) {
          this.stopPoll()
          this.sessionEpoch++
          this.selectedSessionId = ''
          this.messages = []
        }
      } catch (error) {
        if (epoch === this.identityEpoch) this.panelError = error.message || '会话列表加载失败'
      }
    },
    async refresh() {
      const epoch = this.identityEpoch
      await this.loadSessions(epoch)
      if (epoch === this.identityEpoch && this.selectedSessionId) await this.selectSession(this.selectedSessionId)
    },
    async newGeneralSession() { await this.createSession({}) },
    async newContextSession(context) {
      if (!context || !context.machineId) return
      await this.createSession({machineId: Number(context.machineId), diagnosisId: context.diagnosisId || undefined,
        alarmId: context.alarmId || undefined})
    },
    async newMachineSession() {
      if (!this.chosenMachineId) return
      await this.newContextSession({machineId: this.chosenMachineId})
    },
    async createSession(payload) {
      if (this.creating) return
      const epoch = this.identityEpoch
      this.creating = true
      this.panelError = ''
      try {
        const session = assistantData(await createAssistantSession(payload))
        if (epoch !== this.identityEpoch || !session || !session.id) return
        this.sessions.unshift(session)
        this.chosenMachineId = null
        await this.selectSession(String(session.id))
      } catch (error) {
        if (epoch === this.identityEpoch) this.panelError = error.message || '创建会话失败'
      } finally {
        if (epoch === this.identityEpoch) this.creating = false
      }
    },
    async selectSession(id) {
      this.stopPoll()
      const epoch = ++this.sessionEpoch
      this.selectedSessionId = String(id)
      this.messages = []
      this.panelError = ''
      this.loading = true
      this.sending = false
      const identity = this.identityEpoch
      try {
        const history = assistantData(await getAssistantHistory(id))
        if (epoch !== this.sessionEpoch || identity !== this.identityEpoch) return
        this.messages = Array.isArray(history) ? history.map(item => ({error: '', sources: [], pollError: false, ...item})) : []
        const active = this.messages.find(item => item.role === 'assistant' &&
          ['PENDING', 'RUNNING'].includes(item.status) && item.taskId)
        if (active) this.schedulePoll(active.taskId, epoch, 0)
        this.scrollBottom()
      } catch (error) {
        if (epoch === this.sessionEpoch && identity === this.identityEpoch) this.panelError = error.message || '聊天记录加载失败'
      } finally {
        if (epoch === this.sessionEpoch && identity === this.identityEpoch) this.loading = false
      }
    },
    machineVisible(visible) { if (visible && !this.machines.length) this.searchMachines('') },
    async searchMachines(keyword) {
      const seq = ++this.machineSeq
      const identity = this.identityEpoch
      this.machineKeyword = keyword || ''
      this.machineLoading = true
      this.machineError = false
      try {
        const response = await fetchGet('/device/web/machine/v3/list', {page: 1, size: 30, machineName: this.machineKeyword}, false, {skipGlobalErrorMessage: true, timeout: 15000})
        if (seq !== this.machineSeq || identity !== this.identityEpoch) return
        const payload = assistantData(response)
        const page = payload && payload.data && !Array.isArray(payload.data) ? payload.data : payload
        this.machines = Array.isArray(page) ? page : (page && (page.content || page.list || page.records || page.data)) || []
      } catch (error) {
        if (seq === this.machineSeq && identity === this.identityEpoch) this.machineError = true
      } finally {
        if (seq === this.machineSeq && identity === this.identityEpoch) this.machineLoading = false
      }
    },
    useQuestion(value) { this.question = value; this.send() },
    onInputKeydown(event) {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
      event.preventDefault()
      this.send()
    },
    makeRequestId() {
      return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    },
    async send() {
      const question = this.question.trim()
      if (!question || question.length > 1000 || !this.currentSession || this.busy) return
      const requestId = this.makeRequestId()
      this.question = ''
      this.messages.push({id: `local-user-${requestId}`, requestId, role: 'user', content: question, createdAt: new Date().toISOString()})
      const pending = {id: `local-reply-${requestId}`, requestId, role: 'assistant', content: '', status: 'PENDING', error: '', sources: [], taskId: null, pollError: false, createdAt: new Date().toISOString()}
      this.messages.push(pending)
      this.scrollBottom()
      await this.submit(pending, question, false)
    },
    async retryMessage(message) {
      if (this.busy || !message.requestId) return
      const user = this.messages.find(item => item.role === 'user' && item.requestId === message.requestId)
      if (!user) return
      message.status = 'PENDING'
      message.error = ''
      await this.submit(message, user.content, !!message.taskId)
    },
    async submit(message, question, retry) {
      const epoch = this.sessionEpoch
      const identity = this.identityEpoch
      const sessionId = this.selectedSessionId
      this.sending = true
      try {
        const task = assistantData(await sendAssistantMessage({sessionId, requestId: message.requestId, question, retry}))
        if (identity !== this.identityEpoch || epoch !== this.sessionEpoch || sessionId !== this.selectedSessionId) return
        if (!this.applyTask(message, task)) throw new Error('助手返回了无效任务')
        if (!terminal.includes(message.status) && message.taskId) this.schedulePoll(message.taskId, epoch)
      } catch (error) {
        if (identity !== this.identityEpoch || epoch !== this.sessionEpoch) return
        message.status = 'FAILED'
        message.pollError = !!message.taskId
        message.error = message.taskId
          ? '重试提交结果未确认，点击“继续查询”查看任务结果。'
          : '提交结果未确认，可使用相同请求号重试：' + (error.message || '网络异常')
      } finally {
        if (identity === this.identityEpoch && epoch === this.sessionEpoch) this.sending = false
      }
    },
    applyTask(message, task) {
      if (!task || String(task.requestId) !== String(message.requestId) ||
        String(task.sessionId) !== this.selectedSessionId || !['PENDING', 'RUNNING', 'SUCCESS', 'FAILED'].includes(task.status) ||
        (['PENDING', 'RUNNING'].includes(task.status) && !task.taskId)) return false
      message.taskId = task.taskId || message.taskId
      message.status = task.status
      message.content = task.answer || ''
      message.error = task.error || ''
      message.sources = task.sources || []
      message.createdAt = task.answeredAt || message.createdAt
      message.pollError = false
      this.scrollBottom()
      return true
    },
    stopPoll() { if (this.pollTimer) clearTimeout(this.pollTimer); this.pollTimer = null },
    schedulePoll(taskId, epoch, wait = delay) {
      this.stopPoll()
      this.pollTimer = setTimeout(() => this.pollTask(taskId, epoch), wait)
    },
    async pollTask(taskId, epoch) {
      if (epoch !== this.sessionEpoch || !this.available) return
      const identity = this.identityEpoch
      const sessionId = this.selectedSessionId
      const message = this.messages.find(item => item.role === 'assistant' && String(item.taskId) === String(taskId))
      if (!message) return
      try {
        const task = assistantData(await getAssistantReply(taskId))
        if (identity !== this.identityEpoch || epoch !== this.sessionEpoch || sessionId !== this.selectedSessionId) return
        if (!this.applyTask(message, task)) throw new Error('助手返回了无效任务')
        if (!terminal.includes(message.status)) this.schedulePoll(taskId, epoch)
      } catch (error) {
        if (identity !== this.identityEpoch || epoch !== this.sessionEpoch) return
        message.status = 'FAILED'
        message.pollError = true
        message.error = '查询回复失败，任务可能仍在运行。点击“继续查询”查看结果。'
      }
    },
    safeSources(sources) {
      return sources.filter(source => {
        if (!source || typeof source.url !== 'string') return false
        const url = source.url
        if (!url.startsWith('/') || url.startsWith('//') || /[%\\#]/.test(url) ||
          Array.from(url).some(character => character.charCodeAt(0) < 32)) return false
        const path = url.split('?')[0]
        if (path.includes('//') || path.split('/').some(segment => segment === '.' || segment === '..')) return false
        return allowedSourcePaths.some(allowed => allowed.endsWith('/')
          ? path.startsWith(allowed)
          : path === allowed || path.startsWith(`${allowed}/`))
      })
    },
    formatTime(value) {
      if (!value) return ''
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    scrollBottom() { this.$nextTick(() => { if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight }) }
  }
}
</script>

<style scoped lang="scss">
.global-assistant { position: fixed; z-index: 1800; right: 20px; bottom: 20px; color: #dceeff; }
.assistant-fab { width: 68px; height: 68px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 2px; border: 1px solid #00c8f3; border-radius: 50%; background: #12314e; box-shadow: 0 8px 25px #071b2b99; color: #e9faff; padding: 0; cursor: grab; touch-action: none; user-select: none; }
.assistant-fab:active { cursor: grabbing; }
.assistant-fab i { font-size: 20px; }
.assistant-fab span { font-size: 11px; white-space: nowrap; }
.assistant-panel { box-sizing: border-box; position: fixed; right: 20px; top: 72px; width: min(440px, calc(100vw - 40px)); height: calc(100vh - 92px); max-height: calc(100vh - 16px); display: flex; flex-direction: column; background: #0e2539; border: 1px solid #137396; border-radius: 12px; box-shadow: 0 12px 40px #07121dcc; overflow: hidden; }
.assistant-header { display: flex; justify-content: space-between; align-items: center; background: #203f72; padding: 12px 16px; cursor: grab; touch-action: none; user-select: none; }
.assistant-header:active { cursor: grabbing; }
.assistant-header strong, .assistant-header small { display: block; }
.assistant-header small { margin-top: 3px; opacity: .75; }
.assistant-header button { background: none; border: 0; color: #e2f6ff; cursor: pointer; touch-action: auto; }
.assistant-toolbar, .assistant-machine, .assistant-context, .assistant-switch { padding: 8px 12px; border-bottom: 1px solid #24506c; font-size: 12px; }
.assistant-toolbar { display: flex; gap: 8px; }
.assistant-toolbar .el-select { flex: 1; min-width: 0; }
.assistant-machine { display: flex; align-items: center; gap: 6px; }
.assistant-machine .el-select { flex: 1; min-width: 0; }
.assistant-switch { color: #ffd59b; }
.assistant-messages { flex: 1; min-height: 0; overflow-y: auto; padding: 12px; }
.assistant-empty { text-align: center; color: #a8bbce; margin: 30px 0; }
.assistant-message { display: flex; margin: 9px 0; }
.assistant-message.is-user { justify-content: flex-end; }
.assistant-bubble { max-width: 88%; min-width: 85px; background: #203f72; border: 1px solid #4e8bb3; border-radius: 12px; padding: 10px; }
.is-user .assistant-bubble { background: #008db6; border-color: #00d4ff; }
.assistant-content { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.5; }
.assistant-state { margin-top: 5px; font-size: 12px; color: #a7d7ee; }
.assistant-state.is-error, .assistant-error-line { color: #ffb6b6; }
.assistant-error-line { padding: 8px 12px; cursor: pointer; font-size: 12px; }
.assistant-sources { margin-top: 8px; font-size: 12px; }
.assistant-sources a { display: inline-block; margin: 0 8px 3px 0; color: #49dcff; }
.assistant-bubble time { display: block; opacity: .6; font-size: 11px; text-align: right; }
.assistant-quick { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 12px; border-top: 1px solid #24506c; }
.assistant-quick button { flex-shrink: 0; border: 1px solid #008eae; background: #183951; color: #38d4ff; border-radius: 5px; cursor: pointer; padding: 5px 7px; font-size: 12px; }
.assistant-compose { flex-shrink: 0; padding: 10px 12px; }
.assistant-compose ::v-deep .el-input__count { background: transparent; color: #98aabc; }
.assistant-compose > div:last-child { display: flex; justify-content: space-between; align-items: center; padding-top: 6px; }
.assistant-compose small { color: #98aabc; }
.assistant-drawer-enter-active, .assistant-drawer-leave-active { transition: transform .2s, opacity .2s; }
.assistant-drawer-enter, .assistant-drawer-leave-to { transform: translateX(20px); opacity: 0; }
@media (max-width: 600px) { .global-assistant { right: 10px; bottom: 10px; } .assistant-panel { right: 16px; top: 16px; width: calc(100vw - 32px); height: calc(100vh - 88px); } .assistant-machine > span { display: none; } }
@media (max-height: 520px) {
  .assistant-panel { top: 8px; height: calc(100vh - 24px); }
  .assistant-header { padding: 8px 12px; }
  .assistant-toolbar, .assistant-machine, .assistant-context, .assistant-switch { padding: 4px 10px; }
  .assistant-quick { max-height: 34px; overflow-y: auto; padding: 5px 10px; }
  .assistant-compose { padding: 6px 10px; }
}
</style>
